import { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import { ErrorBanner, InfoBanner } from './Feedback';
import { formatBytes } from '../bytes';
import { api } from '../api/client';

/**
 * Moving a library that already exists onto Vimeo.
 *
 * <p>There is a Send to Vimeo on every file row, and for a migration it is useless:
 * it lives inside one chapter's list, so moving an existing catalogue would mean
 * walking every chapter of every book and remembering which ones were done. This asks
 * the same question once — what is left — and answers it for everything at once.
 *
 * <p>It refreshes on a timer while anything is moving, because the server tells us
 * nothing: a transcode finishes on Vimeo's schedule and the only way to know is to
 * ask. The timer stops when the backlog is empty and nothing is in flight, so an open
 * tab is not polling forever over a finished job.
 */
export default function VimeoMigrationModal({ onClose, onChanged }) {
  const [backlog, setBacklog] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [domainNote, setDomainNote] = useState(null);

  const load = useCallback(async () => {
    try {
      setBacklog(await api.vimeoBacklog());
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const moving = (backlog?.inFlightCount ?? 0) > 0;
  useEffect(() => {
    if (!moving) return undefined;
    const handle = setInterval(load, 5000);
    return () => clearInterval(handle);
  }, [moving, load]);

  async function sendAll() {
    setBusy(true);
    setError(null);
    try {
      await api.sendAllToVimeo();
      await load();
      onChanged?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Re-applies the whitelist to videos that are already up there.
   *
   * <p>Here rather than in a settings screen because this is where somebody stands
   * when the partner site refuses to play: the domains are listed a line above, and
   * the reason a new one does nothing on its own is that the whitelist is written when
   * the video is created.
   */
  async function refreshDomains() {
    setBusy(true);
    setError(null);
    setDomainNote(null);
    try {
      const { queued } = await api.refreshVimeoDomains();
      setDomainNote(
        queued === 0
          ? 'There are no videos on Vimeo yet, so there is nothing to re-apply.'
          : `Re-applying on ${queued.toLocaleString()} ${queued === 1 ? 'video' : 'videos'}. `
            + 'This runs on the server and takes a request per video per domain; '
            + 'anything that could not be reached is named in the log.',
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function sendOne(resourceId) {
    setBusy(true);
    setError(null);
    try {
      await api.sendResourceToVimeo(resourceId);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const waiting = backlog?.waiting ?? [];

  return (
    <Modal
      title="Move existing files to Vimeo"
      subtitle="Video and audio already uploaded here, still served from local storage."
      width={760}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || waiting.length === 0 || !backlog?.configured}
            onClick={sendAll}
          >
            {busy
              ? 'Queueing…'
              : `Send all ${waiting.length || ''}`.trim()}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* Said plainly rather than left as an empty list that reads like success. */}
      {backlog && !backlog.configured && <InfoBanner>{backlog.readiness}</InfoBanner>}

      {backlog && (
        <div className="stat-row" style={{ marginBottom: 'var(--s1)' }}>
          <div className="stat">
            <b>{backlog.waitingCount.toLocaleString()}</b>
            <span>Still here{backlog.waitingBytes ? ` · ${formatBytes(backlog.waitingBytes)}` : ''}</span>
          </div>
          <div className="stat">
            <b>{backlog.inFlightCount.toLocaleString()}</b>
            <span>Moving now</span>
          </div>
          <div className="stat">
            <b>{backlog.doneCount.toLocaleString()}</b>
            <span>On Vimeo</span>
          </div>
        </div>
      )}

      {/* Where a video will and will not play, and the control that makes a newly
          added domain reach the videos that pre-date it. Independent of the backlog,
          so it stays after everything has moved — which is exactly when somebody is
          wiring up a second site. */}
      {backlog?.configured && (
        <div className="vimeo-domains">
          <div>
            <b>Plays on</b>
            <ul className="chip-list">
              {backlog.embedDomains?.length ? (
                backlog.embedDomains.map((domain) => (
                  <li key={domain} className="badge">
                    <span className="code-text">{domain}</span>
                  </li>
                ))
              ) : (
                <li className="muted small">nowhere — no domain is configured</li>
              )}
            </ul>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={refreshDomains}
          >
            Re-apply domains
          </button>
        </div>
      )}

      {domainNote && <InfoBanner>{domainNote}</InfoBanner>}

      {backlog === null && <div className="empty">Loading…</div>}

      {backlog && waiting.length === 0 && (
        <div className="empty">
          {backlog.inFlightCount > 0
            ? 'Everything is queued. This updates as each one finishes.'
            : 'Nothing left here — every video and audio file is on Vimeo.'}
        </div>
      )}

      {waiting.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>File</th>
                <th>Where</th>
                <th>Size</th>
                <th aria-label="Send" />
              </tr>
            </thead>
            <tbody>
              {waiting.map((row) => (
                <tr key={row.resourceId}>
                  <td>
                    {row.name}
                    <span className="muted small"> · {row.typeLabel}</span>
                    {/* A failed one is in this list because it needs sending again,
                        which reads as "never sent" without saying so. */}
                    {row.vimeoStatus === 'FAILED' && (
                      <span className="badge badge-danger" style={{ marginLeft: 6 }}>
                        Failed
                      </span>
                    )}
                  </td>
                  <td className="muted small">
                    {row.titleName}
                    <br />
                    Chapter {row.chapterSequence} · {row.chapterName}
                  </td>
                  <td>{formatBytes(row.sizeBytes)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busy || !backlog.configured}
                      onClick={() => sendOne(row.resourceId)}
                    >
                      {row.vimeoStatus === 'FAILED' ? 'Retry' : 'Send'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="hint" style={{ marginTop: 'var(--s1)' }}>
        Two go at a time, and each keeps its local copy until Vimeo finishes
        transcoding — so a file is never in neither place, and teachers and students
        keep watching the local copy while it moves. Closing this does not stop
        anything; the queue is on the server.
      </p>
      <p className="hint">
        A video is only playable on the domains above. Adding one to the deployment
        does not reach videos already uploaded — the whitelist is written when the
        video is created — so press <b>Re-apply domains</b> after changing it. Note
        what a second domain costs: the embed link does not expire, and the domain list
        is the only thing stopping it playing elsewhere.
      </p>
    </Modal>
  );
}
